export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface FeedParams extends PaginationParams {
  contentType?: string;
}

export interface CheckInsParams extends PaginationParams {
  restaurantId?: number;
  fromConnections?: boolean;
  days?: number;
}

export interface UserSearchParams {
  q?: string;
  name?: string;
  location?: string;
  limit?: number;
  friendsOnly?: boolean;
  /** Existing member IDs to check mutual friendships against */
  memberIds?: number[];
}

export interface NotificationsParams extends PaginationParams {
  isRead?: boolean;
}

export const socialKeys = {
  all: ['social'] as const,

  // Posts
  posts: () => [...socialKeys.all, 'posts'] as const,
  userPosts: (userId: number, params?: PaginationParams) =>
    [...socialKeys.posts(), 'user', userId, params] as const,
  post: (urlKey: string) => [...socialKeys.posts(), urlKey] as const,

  // Feed
  feed: () => [...socialKeys.all, 'feed'] as const,
  feedList: (params?: FeedParams) => [...socialKeys.feed(), 'list', params] as const,
  feedActivity: (urlKey: string) => [...socialKeys.feed(), 'activity', urlKey] as const,

  // Check-ins
  checkIns: () => [...socialKeys.all, 'checkins'] as const,
  userCheckIns: (userId: number, params?: PaginationParams) =>
    [...socialKeys.checkIns(), 'user', userId, params] as const,
  checkInsList: (params?: CheckInsParams) => [...socialKeys.checkIns(), 'list', params] as const,

  // Relationships
  relationships: () => [...socialKeys.all, 'relationships'] as const,
  friends: () => [...socialKeys.relationships(), 'friends'] as const,
  userFriends: (userId: number) => [...socialKeys.friends(), userId] as const,
  followers: () => [...socialKeys.relationships(), 'followers'] as const,
  userFollowers: (userId: number) => [...socialKeys.followers(), userId] as const,
  following: () => [...socialKeys.relationships(), 'following'] as const,
  userFollowing: (userId: number) => [...socialKeys.following(), userId] as const,

  // User search and profiles
  users: () => [...socialKeys.all, 'users'] as const,
  userSearch: (params: UserSearchParams) => [...socialKeys.users(), 'search', params] as const,
  userProfile: (userId: number) => [...socialKeys.users(), 'profile', userId] as const,
  userSuggestions: () => [...socialKeys.users(), 'suggestions'] as const,

  // Comments
  comments: () => [...socialKeys.all, 'comments'] as const,
  postComments: (postId: number) => [...socialKeys.comments(), 'post', postId] as const,
  activityComments: (activityId: number | string) =>
    [...socialKeys.comments(), 'activity', activityId] as const,

  // Notifications
  notifications: () => [...socialKeys.all, 'notifications'] as const,
  notificationsList: (params?: NotificationsParams) =>
    [...socialKeys.notifications(), 'list', params] as const,
  unreadCount: () => [...socialKeys.notifications(), 'unread'] as const,
  preferences: () => [...socialKeys.notifications(), 'preferences'] as const,

  // Blocked users and reports
  blocked: () => [...socialKeys.all, 'blocked'] as const,
  reports: () => [...socialKeys.all, 'reports'] as const,

  // Stories
  stories: () => [...socialKeys.all, 'stories'] as const,
  storiesFeed: () => [...socialKeys.stories(), 'feed'] as const,
  myStories: () => [...socialKeys.stories(), 'my'] as const,
  userStories: (userId: number) => [...socialKeys.stories(), 'user', userId] as const,
  story: (storyId: number) => [...socialKeys.stories(), storyId] as const,
  storyViews: (storyId: number) => [...socialKeys.stories(), storyId, 'views'] as const,
} as const;
